import useChatWidget from '@cobliteam/hooks/src/useChatWidget';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Icon from '@cobliteam/ui/icons';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

const APP_BODY_ID = 'app-body';
const OFFSET = 20;
/** Matches useChatWidget: chat bottom when footer visible / not visible; + chat height + 10px gap above chat */
const CHAT_BOTTOM_FOOTER_VISIBLE = 76;
const CHAT_BOTTOM_DEFAULT = 10;
const CHAT_WIDGET_HEIGHT = 56;
const GAP_ABOVE_CHAT = 10;
/** Matches useChatWidget: 0.4s cubic-bezier for position; 0.3s for appear/exit */
const TRANSITION = { duration: 0.35, ease: [0.16, 1, 0.3, 1] as const };

const getScrollElement = () => document.getElementById(APP_BODY_ID);

const PageScrollToTop = () => {
  const { t } = useTranslation('ui-business');
  const { isFooterVisible } = useChatWidget();
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<{ right: number; bottom: number } | null>(null);

  const bottomOffset = useMemo(
    () => (isFooterVisible ? CHAT_BOTTOM_FOOTER_VISIBLE + CHAT_WIDGET_HEIGHT + GAP_ABOVE_CHAT : CHAT_BOTTOM_DEFAULT + CHAT_WIDGET_HEIGHT + GAP_ABOVE_CHAT),
    [isFooterVisible]
  );

  const updateVisibility = useCallback(() => {
    const el = getScrollElement();
    if (el) {
      setVisible(el.scrollTop > 0);
    } else {
      setVisible(window.scrollY > 0);
    }
  }, []);

  const updatePosition = useCallback(() => {
    const el = getScrollElement();
    if (!el) {
      setPosition(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setPosition({
      right: window.innerWidth - rect.right + OFFSET,
      bottom: bottomOffset,
    });
  }, [bottomOffset]);

  useEffect(() => {
    const el = getScrollElement();
    const target = el ?? window;
    const onScroll = () => {
      updateVisibility();
      if (el) updatePosition();
    };
    updateVisibility();
    updatePosition();
    target.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updatePosition);
    return () => {
      target.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updatePosition);
    };
  }, [updateVisibility, updatePosition]);

  const scrollToTop = useCallback(() => {
    const el = getScrollElement();
    if (el) {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const right = position?.right ?? OFFSET;
  const bottom = position?.bottom ?? bottomOffset;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            bottom,
          }}
          exit={{ opacity: 0 }}
          transition={TRANSITION}
          style={{ position: 'fixed', right, zIndex: 10000 }}
          className='rounded-full shadow-lg ring-1 ring-gray-6'
        >
          <Tooltip content={t('scroll_to_top')} align='end'>
            <Button
              variant='surface'
              color='primary'
              size='regular'
              icon={<Icon name='chevron-up' />}
              onClick={scrollToTop}
              aria-label={t('scroll_to_top')}
              data-testid='page-scroll-to-top'
              className='rounded-full'
            />
          </Tooltip>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PageScrollToTop;
